import { START_OVER_ID, WORKFLOW_LIMITS, type WorkflowTranslationLanguage } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Input, Textarea, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../../lib/api-errors";
import { mergeDraft, setTranslation, translationOf, type SourceString } from "../translations-model";
import type { Definition } from "../types";
import { useDraftTranslations } from "../use-workflows-p4";

const T = "engage.workflows.translations";
const LIMIT = { text: WORKFLOW_LIMITS.message, button: WORKFLOW_LIMITS.buttonLabel, field_label: WORKFLOW_LIMITS.fieldLabel, label: WORKFLOW_LIMITS.buttonLabel } as const;

interface StringRowProps {
  language: WorkflowTranslationLanguage;
  string: SourceString;
  value: string;
  drafted: boolean;
  disabled: boolean;
  onEdit: (value: string) => void;
}

function StringRow({ language, string, value, drafted, disabled, onEdit }: StringRowProps) {
  const { t } = useTranslation();
  const label = t(`${T}.translationOf`, { language: t(`${T}.languages.${language}`), text: string.text });
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="translation-row">
      <div className="rounded-md bg-sunken p-2 text-xs">
        <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-fg-muted">{t(`${T}.kinds.${string.kind}`)}</span>
        <span className="whitespace-pre-wrap">{string.text}</span>
      </div>
      <div className="space-y-1">
        {string.kind === "text" ? (
          <Textarea aria-label={label} rows={2} className="text-xs" maxLength={LIMIT.text} disabled={disabled} value={value} onChange={(e) => { onEdit(e.target.value); }} />
        ) : (
          <Input aria-label={label} className="h-9 text-xs" maxLength={LIMIT[string.kind]} disabled={disabled} value={value} onChange={(e) => { onEdit(e.target.value); }} />
        )}
        {value === "" ? <Badge data-testid="translation-fallback">{t(string.buttonId === START_OVER_ID ? `${T}.fallbackBuiltIn` : `${T}.fallback`)}</Badge> : null}
        {drafted ? <Badge tone="brand" data-testid="translation-drafted"><Icon name="sparkles" className="size-3" />{t(`${T}.aiDraft`)}</Badge> : null}
      </div>
    </div>
  );
}

interface LanguageSectionProps {
  workflowId: string;
  language: WorkflowTranslationLanguage;
  definition: Definition;
  strings: readonly SourceString[];
  missing: number;
  drafted: ReadonlySet<string>;
  disabled: boolean;
  onChange: (definition: Definition) => void;
  /** Texts drafted by the assistant were merged into the definition (the edit history starts again from there). */
  onDraftApplied?: () => void;
  onDrafted: (keys: string[]) => void;
  onEdited: (key: string) => void;
}

/** One language: its texts, and "Draft with AI" for the ones that are still missing (the author reviews them before saving). */
export function LanguageSection({ workflowId, language, definition, strings, missing, drafted, disabled, onChange, onDraftApplied, onDrafted, onEdited }: LanguageSectionProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const draft = useDraftTranslations(workflowId);
  const runDraft = () => {
    draft.mutate({ language, definition }, {
      onSuccess: (result) => {
        const merged = mergeDraft(definition, language, result.translations);
        if (merged.keys.length === 0) { toast.info(t(`${T}.draftNothing`)); return; }
        onChange(merged.definition);
        onDraftApplied?.();
        onDrafted(merged.keys);
        toast.success(t(`${T}.drafted`, { count: merged.keys.length }));
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const steps = [...new Set(strings.map((string) => string.stepId))];
  return (
    <section className="space-y-4 rounded-md border border-line p-3" aria-labelledby={`translations-${language}`} data-testid={`translations-${language}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`translations-${language}`} className="flex items-center gap-2 text-sm font-bold">
          {t(`${T}.languages.${language}`)}
          {missing > 0 ? <Badge tone="warning">{t(`${T}.missing`, { count: missing })}</Badge> : <Badge tone="success">{t(`${T}.complete`)}</Badge>}
        </h3>
        {disabled ? null : (
          <Button type="button" variant="secondary" size="sm" loading={draft.isPending} disabled={missing === 0} onClick={runDraft}>
            <Icon name="sparkles" />
            {t(`${T}.draftWithAi`)}
          </Button>
        )}
      </div>
      {steps.map((stepId) => {
        const group = strings.filter((string) => string.stepId === stepId);
        const first = group[0];
        if (!first) return null;
        return (
          <div key={stepId} className="space-y-2">
            <h4 className="text-xs font-bold text-fg-muted">{t(`${T}.stepTitle`, { path: first.path, id: stepId })}</h4>
            {group.map((string) => (
              <StringRow
                key={string.key}
                language={language}
                string={string}
                value={translationOf(definition, language, string)}
                drafted={drafted.has(`${language}:${string.key}`)}
                disabled={disabled}
                onEdit={(value) => { onEdited(string.key); onChange(setTranslation(definition, language, string, value)); }}
              />
            ))}
          </div>
        );
      })}
    </section>
  );
}
